import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  PIPELINE_STAGE_LABELS, advanceCandidatesStage, deleteJobCandidate,
  downloadCsv, getFormFields, getPipelineStageLink, listJobCandidates, sendFormInvites,
  sendInterviewInvites, sendTestInvites, STAGE_THRESHOLD_METRICS
} from "../services/jobService";
import FormFieldsEditorModal from "./FormFieldsEditorModal";
import { StageThresholdsModal } from "./StageThresholdsEditor";
import AssessmentManagerModal from "./AssessmentManagerModal";
import InterviewQuestionsModal from "./InterviewQuestionsModal";

const POLL_MS = 4000;
const ASSESSMENT_STAGE_KEYS = new Set(["mcq", "coding", "both"]);
// Stages whose candidate-facing link is the same for everyone (copyable).
const LINK_STAGE_KEYS = new Set(["mcq", "coding", "both", "ai"]);

function scoreColor(v) {
  if (v == null) return "pending";
  if (v >= 85) return "good";
  if (v >= 60) return "warn";
  return "bad";
}

function scoreReasonTooltip(c) {
  const lines = [];
  if (c.scoreExplanation) lines.push(c.scoreExplanation);
  if (c.matchedSkills?.length) lines.push(`Matched: ${c.matchedSkills.join(", ")}`);
  if (c.missingSkills?.length) lines.push(`Missing: ${c.missingSkills.join(", ")}`);
  return lines.join("\n\n");
}

// Score columns, appended stage by stage: a stage tab shows every score
// column up to and including that stage; "All" and "Final Ranking" show all.
// Values come from pipeline_sync.py (stageScores / finalScore on each row).
const SCORE_COLUMNS = [
  { key: "mcq", label: "MCQ %", stages: ["mcq", "both"],
    get: (c) => c.stageScores?.mcq?.score,
    sub: (c) => (c.stageScores?.mcq?.total != null ? `${c.stageScores.mcq.correct ?? 0}/${c.stageScores.mcq.total} correct` : "") },
  { key: "coding", label: "Coding %", stages: ["coding", "both"],
    get: (c) => c.stageScores?.coding?.score,
    sub: (c) => (c.stageScores?.coding?.total != null ? `${c.stageScores.coding.solved ?? 0}/${c.stageScores.coding.total} solved` : "") },
  { key: "ai", label: "AI Interview", stages: ["ai"],
    get: (c) => c.stageScores?.ai?.overall,
    sub: (c) => {
      const a = c.stageScores?.ai;
      return a ? `Tech ${a.technical ?? "—"} · Comm ${a.communication ?? "—"} · Conf ${a.confidence ?? "—"}` : "";
    } }
];

function integrityOf(c, stages) {
  const s = c.stageScores || {};
  const vals = [];
  if ((stages.includes("mcq") || stages.includes("both")) && s.mcq?.integrity != null) vals.push(s.mcq.integrity);
  if ((stages.includes("coding") || stages.includes("both")) && s.coding?.integrity != null) vals.push(s.coding.integrity);
  if (stages.includes("ai") && s.ai?.integrity != null) vals.push(s.ai.integrity);
  return vals.length ? Math.min(...vals) : null;
}

// Primary sort metric for each stage tab.
function sortValue(c, stageFilter) {
  switch (stageFilter) {
    case "resume": case "form": return c.resumeScore;
    case "mcq": return c.stageScores?.mcq?.score;
    case "coding": return c.stageScores?.coding?.score;
    case "both": {
      const m = c.stageScores?.mcq?.score; const k = c.stageScores?.coding?.score;
      return m == null && k == null ? null : ((m ?? 0) + (k ?? 0)) / 2;
    }
    case "ai": return c.stageScores?.ai?.overall;
    default: return c.finalScore ?? c.resumeScore;
  }
}

const SORT_LABELS = {
  resume: "resume score", form: "resume score", mcq: "MCQ score", coding: "coding score",
  both: "MCQ + coding score", ai: "AI interview score", rank: "overall score", all: "overall score"
};

function metricLabel(stage, metric) {
  return (STAGE_THRESHOLD_METRICS[stage] || []).find(([k]) => k === metric)?.[1] || metric;
}

function formatFormValue(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

function ScoreCell({ value, sub }) {
  return (
    <td>
      <span className={`pipeline-score-cell ${scoreColor(value)}`}>{value == null ? "—" : Math.round(value)}</span>
      {value != null && sub && <span className="pipeline-score-sub">{sub}</span>}
    </td>
  );
}

function formatDateAdded(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// Candidate table for pipelineStages jobs. Sorted by Resume Score (desc, so
// the strongest matches surface first) rather than upload order — this feeds
// filtering decisions per the recruiter's request, so ranking order matters.
// Note: with 100+ candidates on one job, sorting only happens within the
// single fetched page (server-side score-sorted pagination would need its
// own Firestore composite index, deliberately avoided given today's index
// incident — worth revisiting if a job's candidate count grows that large).
export default function PipelineCandidateTable({ jobId, job, onCandidateCountChange }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [removingId, setRemovingId] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [advancing, setAdvancing] = useState(false);
  const [sending, setSending] = useState(false);
  const [editingFields, setEditingFields] = useState(false);
  const [managingAssessment, setManagingAssessment] = useState(false);
  const [managingInterview, setManagingInterview] = useState(false);
  const [editingThresholds, setEditingThresholds] = useState(false);
  const [savedThresholds, setSavedThresholds] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [unmatched, setUnmatched] = useState([]);
  const [formFields, setFormFields] = useState([]);

  const pipelineStages = job?.pipelineStages || [];
  const assessmentKind = pipelineStages.find((s) => ASSESSMENT_STAGE_KEYS.has(s)) || null;

  // sync=true asks the server to pull in the latest MCQ/Coding/AI results and
  // apply stage thresholds first — used on open and on "Refresh Scores", not
  // for the lightweight background polling below.
  const load = useCallback(async (silent = false, sync = false) => {
    try {
      if (!silent) setLoading(true);
      const { candidates: rows, unmatched: unmatchedRows } = await listJobCandidates(jobId, { sync });
      setCandidates(rows);
      if (sync) setUnmatched(unmatchedRows);
      setError("");
    } catch (err) {
      if (!silent) setError(err.message || "Unable to load candidates");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { if (jobId) load(false, true); }, [jobId, load]);

  const hasFormStage = pipelineStages.includes("form");
  useEffect(() => {
    if (!jobId || !hasFormStage) return;
    getFormFields(jobId).then(setFormFields).catch(() => setFormFields([]));
  }, [jobId, hasFormStage]);

  const handleRefreshScores = async () => {
    setRefreshing(true);
    await load(true, true);
    setRefreshing(false);
    toast.success("Scores refreshed");
  };

  useEffect(() => {
    if (!jobId) return undefined;
    const hasActive = candidates.some((c) => c.status === "pending" || c.status === "processing");
    if (!hasActive) return undefined;
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [jobId, candidates, load]);

  useEffect(() => { onCandidateCountChange?.(candidates.length); }, [candidates, onCandidateCountChange]);

  // Selection can go stale once polling/reload swaps the candidates array
  // (e.g. a selected row got removed) — drop any ids that no longer exist.
  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set(candidates.map((c) => c.candidateId));
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [candidates]);

  const stageCounts = useMemo(() => {
    const counts = {};
    for (const c of candidates) counts[c.stage || "resume"] = (counts[c.stage || "resume"] || 0) + 1;
    return counts;
  }, [candidates]);

  const filteredCandidates = useMemo(
    () => (stageFilter === "all" ? candidates : candidates.filter((c) => (c.stage || "resume") === stageFilter)),
    [candidates, stageFilter]
  );

  const sortedCandidates = useMemo(
    () => [...filteredCandidates].sort((a, b) => (sortValue(b, stageFilter) ?? -1) - (sortValue(a, stageFilter) ?? -1)),
    [filteredCandidates, stageFilter]
  );

  // Stages whose score columns are visible in the current tab.
  const visibleStages = useMemo(() => {
    if (stageFilter === "all" || stageFilter === "rank") return pipelineStages;
    const idx = pipelineStages.indexOf(stageFilter);
    return idx === -1 ? pipelineStages : pipelineStages.slice(0, idx + 1);
  }, [stageFilter, pipelineStages]);
  const visibleScoreColumns = SCORE_COLUMNS.filter((col) => col.stages.some((s) => visibleStages.includes(s)));
  const showIntegrity = visibleScoreColumns.length > 0;
  const showOverall = visibleStages.some((s) => s !== "resume" && s !== "form" && s !== "rank");
  const isRankView = stageFilter === "rank";
  const allFormFields = useMemo(() => {
    if (!hasFormStage) return [];
    if (formFields.length) return formFields.map((f) => ({ key: f.key, label: f.label || f.key }));
    const keys = new Set();
    candidates.forEach((c) => Object.keys(c.formResponse || {}).forEach((k) => keys.add(k)));
    return [...keys].map((k) => ({ key: k, label: k }));
  }, [hasFormStage, formFields, candidates]);
  const rankFormFields = isRankView ? allFormFields : [];

  // Only a single, specific stage tab (not "All") has an unambiguous "next
  // stage" to advance into — resume is always first, rank is always last.
  const nextStage = useMemo(() => {
    if (stageFilter === "all") return null;
    const idx = pipelineStages.indexOf(stageFilter);
    if (idx === -1 || idx >= pipelineStages.length - 1) return null;
    return pipelineStages[idx + 1];
  }, [stageFilter, pipelineStages]);

  const toggleSelect = (candidateId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(candidateId)) next.delete(candidateId); else next.add(candidateId);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const visibleIds = sortedCandidates.map((c) => c.candidateId);
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(visibleIds);
    });
  };

  const handleRemove = async (candidateId) => {
    setRemovingId(candidateId);
    try {
      await deleteJobCandidate(jobId, candidateId);
      setCandidates((rows) => rows.filter((r) => r.candidateId !== candidateId));
      toast.success("Removed");
    } catch (err) {
      toast.error(err.message || "Unable to remove candidate");
    } finally {
      setRemovingId(null);
    }
  };

  const handleAdvance = async () => {
    if (!nextStage || !selectedIds.size) return;
    setAdvancing(true);
    try {
      const ids = [...selectedIds];
      await advanceCandidatesStage(jobId, ids, nextStage);
      setCandidates((rows) => rows.map((r) => (ids.includes(r.candidateId) ? { ...r, stage: nextStage } : r)));
      setSelectedIds(new Set());
      toast.success(
        `Moved ${ids.length} candidate${ids.length === 1 ? "" : "s"} to ${PIPELINE_STAGE_LABELS[nextStage] || nextStage}. `
        + "This only updates their pipeline stage — it does not send anything to the candidate yet."
      );
    } catch (err) {
      toast.error(err.message || "Unable to advance candidates");
    } finally {
      setAdvancing(false);
    }
  };

  // Shared by Send Form / Send Test Invite / Send Interview Invite — each
  // backend route returns the same { sent, failed } per-candidate shape.
  const runBulkSend = async (sendFn, fallbackError) => {
    if (!selectedIds.size) return;
    setSending(true);
    try {
      const ids = [...selectedIds];
      const { sent, failed } = await sendFn(jobId, ids);
      if (sent.length) toast.success(`Sent to ${sent.length} candidate${sent.length === 1 ? "" : "s"}.`);
      if (failed.length) {
        toast.error(
          `${failed.length} failed: ${failed[0].error}${failed.length > 1 ? ` (+${failed.length - 1} more)` : ""}`
        );
      }
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(err.message || fallbackError);
    } finally {
      setSending(false);
    }
  };

  const handleCopyStageLink = async () => {
    try {
      const link = await getPipelineStageLink(jobId, stageFilter);
      if (!link) {
        toast.error("Add this stage's questions first (Manage Questions), then copy its link.");
        return;
      }
      await navigator.clipboard.writeText(link);
      toast.success(`${PIPELINE_STAGE_LABELS[stageFilter]} link copied`);
    } catch (err) {
      toast.error(err.message || "Could not copy link");
    }
  };

  const handleExport = () => {
    const has = (...keys) => keys.some((k) => pipelineStages.includes(k));
    const rows = sortedCandidates.map((c, i) => ({ ...c, _rank: i + 1 }));
    const columns = [
      ...(isRankView ? [["Rank", (c) => c._rank]] : []),
      ["Candidate", (c) => c.name || "Unnamed candidate"],
      ["Email", (c) => c.email || ""],
      ["Phone", (c) => c.phone || ""],
      ["Experience (yrs)", (c) => c.experienceYears ?? ""],
      ["Skills", (c) => (c.skills || []).join("; ")],
      ["Resume Score", (c) => c.resumeScore ?? ""],
      ["Score Reason", (c) => c.scoreExplanation || ""],
      ...(has("mcq", "both") ? [["MCQ %", (c) => c.stageScores?.mcq?.score ?? ""]] : []),
      ...(has("coding", "both") ? [["Coding %", (c) => c.stageScores?.coding?.score ?? ""]] : []),
      ...(has("ai") ? [
        ["AI Overall", (c) => c.stageScores?.ai?.overall ?? ""],
        ["AI Technical", (c) => c.stageScores?.ai?.technical ?? ""],
        ["AI Communication", (c) => c.stageScores?.ai?.communication ?? ""],
        ["AI Confidence", (c) => c.stageScores?.ai?.confidence ?? ""]
      ] : []),
      ...(has("mcq", "coding", "both", "ai") ? [["Integrity", (c) => integrityOf(c, pipelineStages) ?? ""]] : []),
      ["Overall Score", (c) => c.finalScore ?? ""],
      ...allFormFields.map((f) => [f.label, (c) => {
        const v = formatFormValue(c.formResponse?.[f.key]);
        return v === "—" ? "" : v;
      }]),
      ["Stage", (c) => PIPELINE_STAGE_LABELS[c.stage] || c.stage || ""],
      ["Stage Result", (c) => (c.stageOutcomes?.[c.stage]?.result === "below_threshold" ? "Below threshold" : "")],
      ["Status", (c) => c.status || ""],
      ["Date Added", (c) => formatDateAdded(c.createdAt)]
    ];
    downloadCsv(`${job?.title || "candidates"}_${isRankView ? "final_ranking" : "candidates"}.csv`, columns, rows);
  };

  if (loading) return <div className="recruiter-empty">Loading candidates…</div>;
  if (error) return <div className="recruiter-alert"><span>{error}</span></div>;

  const allVisibleSelected = sortedCandidates.length > 0 && sortedCandidates.every((c) => selectedIds.has(c.candidateId));

  return (
    <div className="pipeline-panel">
      <div className="summary-card-grid pipeline-stage-grid">
        <div
          className={`summary-card pipeline-stage-card${stageFilter === "all" ? " summary-card--active" : ""}`}
          role="button" tabIndex={0}
          onClick={() => setStageFilter("all")}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setStageFilter("all")}
        >
          <span>All candidates</span>
          <strong>{candidates.length}</strong>
          <small>every stage</small>
        </div>
        {pipelineStages.map((key, i) => (
          <div
            key={key}
            className={`summary-card pipeline-stage-card${stageFilter === key ? " summary-card--active" : ""}`}
            role="button" tabIndex={0}
            onClick={() => setStageFilter(key)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setStageFilter(key)}
          >
            <span>{String(i + 1).padStart(2, "0")} · {PIPELINE_STAGE_LABELS[key] || key}</span>
            <strong>{stageCounts[key] || 0}</strong>
            <small>{key === "rank" ? "finalists" : "candidates"}</small>
          </div>
        ))}
      </div>

      <div className="pipeline-table-toolbar">
        <span className="rd-muted">
          {sortedCandidates.length} candidate{sortedCandidates.length === 1 ? "" : "s"}
          {stageFilter !== "all" && ` in ${PIPELINE_STAGE_LABELS[stageFilter] || stageFilter}`} · sorted by {SORT_LABELS[stageFilter] || "score"}
        </span>
        <div className="rd-inline-actions">
          <button type="button" className="secondary-button" onClick={handleRefreshScores} disabled={refreshing}>
            {refreshing ? "Refreshing…" : "Refresh Scores"}
          </button>
          <button type="button" className="secondary-button" onClick={() => setEditingThresholds(true)}>
            Thresholds
          </button>
          {stageFilter === "form" && (
            <button type="button" className="secondary-button" onClick={() => setEditingFields(true)}>
              Edit Form Fields
            </button>
          )}
          {ASSESSMENT_STAGE_KEYS.has(stageFilter) && (
            <button type="button" className="secondary-button" onClick={() => setManagingAssessment(true)}>
              Manage Questions
            </button>
          )}
          {stageFilter === "ai" && (
            <button type="button" className="secondary-button" onClick={() => setManagingInterview(true)}>
              Manage Questions
            </button>
          )}
          {LINK_STAGE_KEYS.has(stageFilter) && (
            <button type="button" className="secondary-button" onClick={handleCopyStageLink}>
              Copy Link
            </button>
          )}
          <button type="button" className="secondary-button" onClick={handleExport} disabled={!sortedCandidates.length}>
            Export CSV
          </button>
        </div>
      </div>

      {unmatched.length > 0 && (
        <div className="pipeline-unmatched-note">
          {unmatched.length} result{unmatched.length === 1 ? "" : "s"} couldn't be matched to a candidate
          {unmatched.some((u) => u.email) && ` (${[...new Set(unmatched.map((u) => u.email).filter(Boolean))].slice(0, 3).join(", ")})`}
          {" "}— the candidate probably signed in with a different email than the one on their resume.
        </div>
      )}

      <StageThresholdsModal
        open={editingThresholds}
        job={savedThresholds ? { ...job, stageConfig: { ...(job?.stageConfig || {}), thresholds: savedThresholds } } : job}
        onClose={() => setEditingThresholds(false)}
        onSaved={(t) => { setSavedThresholds(t); setTimeout(() => load(true, true), 1500); }}
      />
      <FormFieldsEditorModal open={editingFields} jobId={jobId} onClose={() => setEditingFields(false)} />
      <AssessmentManagerModal
        open={managingAssessment}
        jobId={jobId}
        kind={ASSESSMENT_STAGE_KEYS.has(stageFilter) ? stageFilter : assessmentKind}
        companyName={job?.companyName}
        jobTitle={job?.title}
        resumeCriteria={job?.resumeCriteria}
        onClose={() => setManagingAssessment(false)}
      />
      <InterviewQuestionsModal
        open={managingInterview}
        jobId={jobId}
        companyName={job?.companyName}
        jobTitle={job?.title}
        resumeCriteria={job?.resumeCriteria}
        onClose={() => setManagingInterview(false)}
      />

      {selectedIds.size > 0 && (
        <div className="pipeline-bulk-bar">
          <span>{selectedIds.size} selected</span>
          {stageFilter === "form" && (
            <button type="button" className="primary-button" disabled={sending}
              onClick={() => runBulkSend(sendFormInvites, "Unable to send form invites")}>
              {sending ? "Sending…" : "Send Form"}
            </button>
          )}
          {ASSESSMENT_STAGE_KEYS.has(stageFilter) && (
            <button type="button" className="primary-button" disabled={sending}
              onClick={() => runBulkSend(sendTestInvites, "Unable to send test invites")}>
              {sending ? "Sending…" : "Send Test Invite"}
            </button>
          )}
          {stageFilter === "ai" && (
            <button type="button" className="primary-button" disabled={sending}
              onClick={() => runBulkSend(sendInterviewInvites, "Unable to send interview invites")}>
              {sending ? "Sending…" : "Send Interview Invite"}
            </button>
          )}
          {nextStage ? (
            <button
              type="button"
              className={stageFilter === "form" || LINK_STAGE_KEYS.has(stageFilter) ? "secondary-button" : "primary-button"}
              disabled={advancing}
              onClick={handleAdvance}
            >
              {advancing ? "Moving…" : `Advance to ${PIPELINE_STAGE_LABELS[nextStage] || nextStage}`}
            </button>
          ) : (
            <span className="rd-muted">
              {stageFilter === "all"
                ? "Pick a specific stage tab above to advance candidates to the next stage."
                : "This is the last stage — nothing further to advance to."}
            </span>
          )}
          <button type="button" className="link-button" onClick={() => setSelectedIds(new Set())}>Clear</button>
        </div>
      )}

      {!sortedCandidates.length ? (
        <div className="recruiter-empty">
          {candidates.length
            ? "No candidates at this stage yet."
            : "No candidates yet. Upload a batch of resumes to get started — scoring happens automatically against this job's criteria."}
        </div>
      ) : (
        <div className="enterprise-table-wrap">
          <table className="enterprise-table pipeline-candidate-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} aria-label="Select all" />
                </th>
                {isRankView && <th>#</th>}
                <th>Candidate</th>
                <th>Experience</th>
                {!isRankView && <th>Skills</th>}
                <th>Resume Score</th>
                {visibleScoreColumns.map((col) => <th key={col.key}>{col.label}</th>)}
                {showIntegrity && <th>Integrity</th>}
                {showOverall && <th>Overall</th>}
                {rankFormFields.map((f) => <th key={`form-${f.key}`}>{f.label}</th>)}
                <th>Stage</th>
                <th>Status</th>
                {!isRankView && <th>Date Added</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {sortedCandidates.map((c, rowIndex) => (
                <tr key={c.candidateId} className={c.possibleDuplicate ? "pipeline-row-duplicate" : ""}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.candidateId)}
                      onChange={() => toggleSelect(c.candidateId)}
                      aria-label={`Select ${c.name || "candidate"}`}
                    />
                  </td>
                  {isRankView && <td><span className="pipeline-rank-num">{rowIndex + 1}</span></td>}
                  <td>
                    <div className="rd-cand-cell">
                      <div className="rd-cand-avatar">{(c.name || "?").slice(0, 2).toUpperCase()}</div>
                      <div className="rd-cand-display">
                        <span className="rd-cand-name">{c.name || "Unnamed candidate"}</span>
                        {c.email && <span className="rd-cand-email">{c.email}</span>}
                        {c.possibleDuplicate && <span className="rd-pill inactive">Possible duplicate</span>}
                      </div>
                    </div>
                  </td>
                  <td>{c.experienceYears ? `${c.experienceYears} yrs` : "—"}</td>
                  {!isRankView && <td className="pipeline-skills-cell">{(c.skills || []).slice(0, 4).join(", ") || "—"}</td>}
                  <td>
                    {c.status === "completed" && c.resumeScore != null ? (
                      <div className="rd-score-bar" title={scoreReasonTooltip(c) || undefined}>
                        <div className="rd-score-track"><span className={`rd-score-fill ${scoreColor(c.resumeScore)}`} style={{ width: `${c.resumeScore}%` }} /></div>
                        <span className={`rd-score-val ${scoreColor(c.resumeScore)}`}>{c.resumeScore}</span>
                        {c.scoreExplanation && <span className="pipeline-score-info" aria-hidden="true">ⓘ</span>}
                      </div>
                    ) : <span className="rd-score-empty">—</span>}
                  </td>
                  {visibleScoreColumns.map((col) => <ScoreCell key={col.key} value={col.get(c)} sub={col.sub(c)} />)}
                  {showIntegrity && <ScoreCell value={integrityOf(c, visibleStages)} />}
                  {showOverall && <ScoreCell value={c.finalScore} />}
                  {rankFormFields.map((f) => {
                    const v = formatFormValue(c.formResponse?.[f.key]);
                    return <td key={`form-${f.key}`} className="pipeline-form-cell" title={v}>{v}</td>;
                  })}
                  <td><span className="rd-tag">{PIPELINE_STAGE_LABELS[c.stage] || c.stage || "—"}</span></td>
                  <td>
                    <span className={`rd-pill ${c.status === "completed" ? "active" : c.status === "failed" ? "inactive" : ""}`}>
                      {c.status === "pending" ? "Pending" : c.status === "processing" ? "Processing" : c.status === "failed" ? "Failed" : "Scored"}
                    </span>
                    {c.status === "failed" && c.error && <div className="rd-muted pipeline-error-note">{c.error}</div>}
                    {c.stageOutcomes?.[c.stage]?.result === "below_threshold" && (
                      <span
                        className="rd-pill warn pipeline-outcome"
                        title={`Below minimum: ${(c.stageOutcomes[c.stage].failed || []).map((m) => metricLabel(c.stage, m)).join(", ")}`}
                      >
                        Below threshold
                      </span>
                    )}
                    {c.autoInviteError && c.autoInviteError.stage === c.stage && (
                      <div className="rd-muted pipeline-error-note">Auto-invite failed — resend manually. {c.autoInviteError.error}</div>
                    )}
                  </td>
                  {!isRankView && <td className="rd-muted">{formatDateAdded(c.createdAt)}</td>}
                  <td>
                    {c.status === "failed" && (
                      <button
                        type="button"
                        className="link-button"
                        disabled={removingId === c.candidateId}
                        onClick={() => handleRemove(c.candidateId)}
                        title="Remove this failed entry (re-upload the same file to retry instead of removing)"
                      >
                        {removingId === c.candidateId ? "Removing…" : "Remove"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
